/**
 * Login Page
 *
 * User login with tenant code, email, and password.
 * Implements two-factor authentication with email OTP.
 * Supports SSO login with Microsoft Entra ID and Google.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginRequestSchema } from '../../../lib/validators/auth';
import type { LoginRequest } from '../../../types/auth.types';
import type { SSOProviderPublic, SSOTenantInfo } from '../../../types/sso.types';
import { SSOProviderType, SSOProviderStatus } from '../../../types/sso.types';
import { useAuth } from '../../../contexts/AuthContext';
import { ssoService } from '../../../api/sso.service';
import { getErrorMessage } from '../../../lib/utils/errors';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { FormField } from '../../../components/forms/FormField';
import { Login2FAModal } from '../../../components/auth/Login2FAModal';
import logoLgImage from '../../../assets/logo-lg.png';

// ============================================================================
// SSO PROVIDER BUTTON COMPONENT
// ============================================================================

interface SSOProviderButtonProps {
  provider: SSOProviderPublic;
  onClick: (provider: SSOProviderPublic) => void;
  disabled?: boolean;
}

const SSOProviderButton: React.FC<SSOProviderButtonProps> = ({ provider, onClick, disabled }) => {
  const isMicrosoft = provider.providerType === SSOProviderType.MICROSOFT_ENTRA;

  return (
    <button
      type="button"
      onClick={() => onClick(provider)}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 border rounded-lg shadow-sm text-sm font-medium hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-raised)', color: 'var(--color-text-secondary)' }}
    >
      {isMicrosoft ? (
        <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
      ) : (
        <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      )}
      <span>Sign in with {provider.displayName}</span>
    </button>
  );
};

// ============================================================================
// LOGIN PAGE COMPONENT
// ============================================================================

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  // SSO state
  const [ssoInfo, setSsoInfo] = useState<SSOTenantInfo | null>(null);
  const [isSSOLoading, setIsSSOLoading] = useState(false);
  const [ssoRedirecting, setSsoRedirecting] = useState(false);

  const {
    loginStep1,
    verifyLogin,
    resendLoginCode,
    pendingLogin2FA,
    cancelLogin2FA,
  } = useAuth();

  // Remember tenant code from localStorage
  const rememberedTenantCode = localStorage.getItem('remembered_tenant_code') || '';

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: {
      tenantCode: rememberedTenantCode,
      email: '',
      password: '',
    },
  });

  const tenantCode = watch('tenantCode');

  // ==========================================================================
  // SSO PROVIDER LOOKUP
  // ==========================================================================

  /**
   * Check for SSO providers when tenant code changes (debounced)
   */
  useEffect(() => {
    const trimmedCode = tenantCode?.trim();
    if (!trimmedCode || trimmedCode.length < 3) {
      setSsoInfo(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSSOLoading(true);
      try {
        const info = await ssoService.getProviders(trimmedCode);
        setSsoInfo(info);
      } catch {
        // Tenant not found or no SSO configured - that's fine
        setSsoInfo(null);
      } finally {
        setIsSSOLoading(false);
      }
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [tenantCode]);

  // ==========================================================================
  // SSO AUTHORIZATION
  // ==========================================================================

  const handleSSOLogin = useCallback(async (provider: SSOProviderPublic) => {
    const trimmedCode = tenantCode?.trim();
    if (!trimmedCode) return;

    setErrorMessage('');
    setSsoRedirecting(true);

    try {
      // Remember tenant code
      localStorage.setItem('remembered_tenant_code', trimmedCode);

      const redirectUri = `${window.location.origin}/auth/sso/callback`;
      const response = await ssoService.authorize({
        tenantCode: trimmedCode,
        providerType: provider.providerType,
        providerId: provider.providerId,
        redirectUri,
      });

      // Redirect to IdP
      window.location.href = response.authorizationUrl;
    } catch (err) {
      const message = getErrorMessage(err);
      setErrorMessage(message || 'Failed to initiate SSO login. Please try again.');
      setSsoRedirecting(false);
    }
  }, [tenantCode]);

  // ==========================================================================
  // PASSWORD LOGIN
  // ==========================================================================

  const onSubmit = async (data: LoginRequest) => {
    setErrorMessage('');
    setIsLoading(true);

    try {
      await loginStep1(data);
      // Remember tenant code for next time
      if (tenantCode) {
        localStorage.setItem('remembered_tenant_code', tenantCode);
      }
      // 2FA modal will open automatically via pendingLogin2FA state
    } catch (error) {
      // Show generic error for security (don't reveal if user exists)
      const message = getErrorMessage(error);
      if (message.toLowerCase().includes('invalid credentials') ||
          message.toLowerCase().includes('tenant not found') ||
          message.toLowerCase().includes('user not found')) {
        setErrorMessage('Invalid tenant code, email, or password. Please try again.');
      } else if (message.toLowerCase().includes('tenant not verified')) {
        setErrorMessage('Your account has not been verified. Please check your email for the verification link.');
      } else if (message.toLowerCase().includes('suspended')) {
        setErrorMessage('Your account has been suspended. Please contact support.');
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (code: string) => {
    setIsVerifying(true);
    setTwoFactorError(null);

    try {
      await verifyLogin(code);
      // Redirect to dashboard or return URL
      const returnUrl = searchParams.get('returnUrl') || '/app/dashboard';
      navigate(returnUrl, { replace: true });
    } catch (error) {
      const message = getErrorMessage(error);
      setTwoFactorError(message);
      throw error; // Re-throw to let modal handle attempt decrement
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resendLoginCode();
      setTwoFactorError(null);
    } catch {
      throw new Error('Failed to resend code');
    }
  };

  const handleClose2FAModal = () => {
    cancelLogin2FA();
    setTwoFactorError(null);
  };

  // ==========================================================================
  // DERIVED STATE
  // ==========================================================================

  const activeProviders = ssoInfo?.providers?.filter((p) => p.status === SSOProviderStatus.ACTIVE) ?? [];
  const hasActiveSSO = activeProviders.length > 0;
  const enforceSSO = ssoInfo?.enforceSSO ?? false;
  const showPasswordForm = !enforceSSO || !hasActiveSSO;

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="flex justify-center">
          <img src={logoLgImage} alt="Ketrics" className="h-24 w-auto" />
        </div>

        {/* Card wrapping form section */}
        <div className="card rounded-xl shadow-card border p-8 space-y-6">
          {/* Tenant name display when SSO is available */}
          {ssoInfo?.tenantName && hasActiveSSO && (
            <div className="text-center">
              <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Sign in to {ssoInfo.tenantName}
              </h2>
            </div>
          )}

          {/* Form */}
          <div className="space-y-6">
            {errorMessage && (
              <Alert variant="error" dismissible onDismiss={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}

            {/* Tenant Code Field (always visible) */}
            <div className="space-y-4">
              <FormField
                label="Tenant Code"
                required
                error={errors.tenantCode?.message}
                helperText="Your organization's unique identifier"
              >
                <Input
                  {...register('tenantCode', {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.toLowerCase();
                    },
                  })}
                  type="text"
                  placeholder="e.g., acme-corp"
                  error={errors.tenantCode?.message}
                  disabled={isLoading || ssoRedirecting}
                />
              </FormField>

              {/* SSO loading indicator */}
              {isSSOLoading && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <Spinner size="sm" />
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Checking for SSO providers...</span>
                </div>
              )}
            </div>

            {/* SSO Buttons */}
            {hasActiveSSO && (
              <div className="space-y-3">
                {activeProviders.map((provider) => (
                  <SSOProviderButton
                    key={provider.providerId}
                    provider={provider}
                    onClick={handleSSOLogin}
                    disabled={isLoading || ssoRedirecting}
                  />
                ))}
              </div>
            )}

            {/* Divider between SSO and password form */}
            {hasActiveSSO && showPasswordForm && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: 'var(--color-border)' }} />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4" style={{ backgroundColor: 'var(--color-bg-raised)', color: 'var(--color-text-secondary)' }}>or</span>
                </div>
              </div>
            )}

            {/* Enforce SSO message */}
            {enforceSSO && hasActiveSSO && (
              <div className="text-center">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Your organization requires SSO for sign-in. Use one of the options above.
                </p>
              </div>
            )}

            {/* Password Login Form */}
            {showPasswordForm && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  label="Email"
                  required
                  error={errors.email?.message}
                >
                  <Input
                    {...register('email')}
                    type="email"
                    placeholder="you@example.com"
                    error={errors.email?.message}
                    disabled={isLoading || ssoRedirecting}
                  />
                </FormField>

                <FormField
                  label="Password"
                  required
                  error={errors.password?.message}
                >
                  <Input
                    {...register('password')}
                    type="password"
                    placeholder="********"
                    error={errors.password?.message}
                    disabled={isLoading || ssoRedirecting}
                  />
                </FormField>

                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <Link
                      to="/forgot-password"
                      className="font-medium text-primary-600 hover:text-primary-500"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                </div>

                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    disabled={isLoading || ssoRedirecting}
                  >
                    Sign in
                  </Button>
                </div>
              </form>
            )}

            <p className="text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <Link to="/request-access" className="font-medium text-primary-600 hover:text-primary-500">
                Request access to Ketrics
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <p>
            Need help?{' '}
            <a
              href="mailto:support@ketrics.com"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>

      {/* SSO Redirecting Overlay */}
      {ssoRedirecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="card rounded-lg p-8 shadow-xl text-center">
            <Spinner size="lg" />
            <p className="mt-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>Redirecting to identity provider...</p>
          </div>
        </div>
      )}

      {/* 2FA Modal */}
      {pendingLogin2FA && (
        <Login2FAModal
          isOpen={true}
          onClose={handleClose2FAModal}
          destination={pendingLogin2FA.destination}
          expiresIn={pendingLogin2FA.expiresIn}
          attemptsRemaining={pendingLogin2FA.attemptsRemaining}
          onSubmit={handleVerify2FA}
          onResendCode={handleResendCode}
          isLoading={isVerifying}
          error={twoFactorError}
        />
      )}
    </div>
  );
};

export default LoginPage;
